import { ROUTES } from '../../../../constants/RouteNameMapping';
import { PanelMetadata, TearsheetPanelType } from './types';

// Valid routes for tearsheet panel configuration - used for validation
export const VALID_TEARSHEET_CONFIG_ROUTES = new Set<ROUTES>([
  ROUTES.TEAR_SHEETS_HEADER_CONFIG,
  ROUTES.TEAR_SHEETS_COMPANY_SUMMARY_CONFIG,
  ROUTES.TEAR_SHEETS_INVESTMENT_THESIS_CONFIG,
  ROUTES.TEAR_SHEETS_PERFORMANCE_COMMENTARY_CONFIG,
  ROUTES.TEAR_SHEETS_COMPANY_DESCRIPTION_CONFIG,
  ROUTES.TEAR_SHEETS_INVESTMENT_SUMMARY_CONFIG,
  ROUTES.TEAR_SHEETS_FINANCING_HISTORY_CONFIG,
  ROUTES.TEAR_SHEETS_PERFORMANCE_SUMMARY_CONFIG,
  ROUTES.TEAR_SHEETS_CAPITALIZATION_SUMMARY_CONFIG,
]);

export function isValidConfigRoute(route: string): route is ROUTES {
  return VALID_TEARSHEET_CONFIG_ROUTES.has(route as ROUTES);
}

// A4 paper dimensions: 210mm x 297mm
export const A4_WIDTH_MM = 210;
export const A4_HEIGHT_MM = 297;
export const A4_ASPECT_RATIO = A4_HEIGHT_MM / A4_WIDTH_MM; // ~1.414

// Grid configuration
export const GRID_COLS = 12;
export const GRID_ROW_HEIGHT = 50;
export const GRID_WIDTH = 600;
export const GRID_MAX_ROWS = 16; // 600 * 1.414 = 848px, 16 * 50 = 800px (fits within A4 height)

export const TEARSHEET_PANELS: Record<TearsheetPanelType, PanelMetadata> = {
  header: {
    type: 'header',
    label: 'Header',
    description: 'Logo and company info display',
    configRoute: ROUTES.TEAR_SHEETS_HEADER_CONFIG,
    defaultSize: { w: 12, h: 2 },
  },
  companySummary: {
    type: 'companySummary',
    label: 'Company Summary',
    description: 'Foundational company data',
    configRoute: ROUTES.TEAR_SHEETS_COMPANY_SUMMARY_CONFIG,
    defaultSize: { w: 6, h: 4 },
  },
  investmentThesis: {
    type: 'investmentThesis',
    label: 'Investment Thesis',
    description: 'Qualitative investment rationale',
    configRoute: ROUTES.TEAR_SHEETS_INVESTMENT_THESIS_CONFIG,
    defaultSize: { w: 6, h: 4 },
  },
  performanceCommentary: {
    type: 'performanceCommentary',
    label: 'Performance Commentary',
    description: 'Text-based performance notes',
    configRoute: ROUTES.TEAR_SHEETS_PERFORMANCE_COMMENTARY_CONFIG,
    defaultSize: { w: 12, h: 3 },
  },
  companyDescription: {
    type: 'companyDescription',
    label: 'Company Description',
    description: 'Narrative company information',
    configRoute: ROUTES.TEAR_SHEETS_COMPANY_DESCRIPTION_CONFIG,
    defaultSize: { w: 12, h: 3 },
  },
  investmentSummary: {
    type: 'investmentSummary',
    label: 'Investment Summary',
    description: 'Financial investment metrics (MOIC, IRR, etc.)',
    configRoute: ROUTES.TEAR_SHEETS_INVESTMENT_SUMMARY_CONFIG,
    defaultSize: { w: 6, h: 4 },
  },
  financingHistory: {
    type: 'financingHistory',
    label: 'Financing History',
    description: 'Round-by-round funding data',
    configRoute: ROUTES.TEAR_SHEETS_FINANCING_HISTORY_CONFIG,
    defaultSize: { w: 6, h: 4 },
  },
  performanceSummary: {
    type: 'performanceSummary',
    label: 'Performance Summary',
    description: 'KPI-based performance metrics',
    configRoute: ROUTES.TEAR_SHEETS_PERFORMANCE_SUMMARY_CONFIG,
    defaultSize: { w: 12, h: 4 },
  },
  capitalizationSummary: {
    type: 'capitalizationSummary',
    label: 'Capitalization Summary',
    description: 'Cap table equity/debt breakdown',
    configRoute: ROUTES.TEAR_SHEETS_CAPITALIZATION_SUMMARY_CONFIG,
    defaultSize: { w: 6, h: 4 },
  },
};

export const PANEL_LIST: PanelMetadata[] = Object.values(TEARSHEET_PANELS);
